/**
 * Pure view helpers for the Bexio settings page.
 *
 * Separated from the page for the same reason `bexioSyncRunsView.js` is: these
 * are total functions over a response, so they can be tested without rendering
 * anything, and the page stays an orchestrator.
 *
 * NOTHING HERE DERIVES READINESS. `config_complete` and `missing_blockers` come
 * from the backend's canonical `missing_config_blockers`, and the helpers below
 * only format them. A second completeness rule in the browser would drift from
 * the backend's the first time a required field is added — which is exactly the
 * failure the draft-preview surface already demonstrates with its stale static
 * blocker list.
 */

/**
 * Render one stored config scalar.
 *
 * THE TRI-STATE IS THE WHOLE POINT. Three of these fields (`mwst_type`,
 * `mwst_is_net`, `show_position_taxes`) are nullable with three meanings, and
 * FAHN runs `mwst_type = 0` with `show_position_taxes = false` in production. So
 * `0` must render as "0" and `false` must render as "Nein" — only `null` and
 * `undefined` are "not configured".
 *
 * Written as explicit identity checks rather than `value || "—"` precisely
 * because that idiom would turn both real values into the em dash and tell an
 * operator their working configuration is missing.
 */
export function formatConfigValue(value) {
  if (value === null || value === undefined) return "—";
  if (value === true) return "Ja";
  if (value === false) return "Nein";
  return String(value);
}

/**
 * Who the invoice is issued under, for the summary strip.
 *
 * Prefers the stored display name and falls back to the bare id, mirroring the
 * draft preview panel — the id alone is honest, whereas inventing a name would
 * not be. The name is display-only and never reaches Bexio.
 */
export function contactPersonLabel(values) {
  const name = values?.target_user_display_name;
  if (name) return name;
  const id = values?.target_user_id;
  if (id === null || id === undefined) return "—";
  return `Bexio-Benutzer #${id}`;
}

/** "Vollständig" / "N Angaben fehlen", from the backend verdict only. */
export function completenessLabel(config) {
  if (!config || !config.configured) return "—";
  if (config.config_complete) return "Vollständig";
  const count = (config.missing_blockers || []).length;
  return count === 1 ? "1 Angabe fehlt" : `${count} Angaben fehlen`;
}

/** Semantic tone for the completeness figure. */
export function completenessTone(config) {
  if (!config || !config.configured) return "neutral";
  return config.config_complete ? "good" : "warn";
}

/**
 * The stored configuration, in the order an operator would work through it.
 *
 * `blocker` names the backend token this row is missing when unset, so a row can
 * be marked without the page pattern-matching on field names. `hint` says what a
 * bare integer actually is: these are Bexio-mandant ids, meaningless outside this
 * organization's Bexio account, and the backend cannot resolve them to names
 * without calling Bexio — which this read path deliberately never does. Showing
 * the id is honest; printing a name next to a bare id would be a guess.
 *
 * The catalog-backed rows are the exception, and only those: their names come
 * from a live provider read (`control: "catalog"`), never from a table in here.
 *
 * `target_user_display_name` is not a row of its own: it is the display
 * companion of `target_user_id` and appears as that row's hint.
 */
export const CONFIG_FIELDS = [
  {
    key: "target_user_id",
    label: "Interner Ansprechpartner",
    hint: "Aus Bexio übernommen",
    blocker: "missing_target_user_id",
    // D3f.2. DELIBERATELY NOT `control: "catalog"`, even though this row is now
    // catalog-backed like the other five. Those five ride the shared form: the
    // page holds their value, `buildDirtyPatch` diffs it and one "Änderungen
    // speichern" sends them all through the provider-free `PATCH /config`.
    // `target_user_id` cannot travel that path — its write must resolve the id
    // against Bexio first — so it gets a control kind of its own, which is what
    // keeps it out of `form`, out of the dirty patch and out of that button.
    control: "targetUser",
    catalog: "users",
  },
  {
    key: "default_language_id",
    label: "Standard-Sprache",
    hint: "Aus Bexio übernommen — Sprache des Belegs",
    blocker: "missing_default_language_id",
    control: "catalog",
    catalog: "languages",
  },
  {
    key: "currency_id",
    label: "Währung",
    hint: "Aus Bexio übernommen",
    blocker: "missing_currency_id",
    control: "catalog",
    // Which provider catalog feeds this row. Named here so the section component
    // picks the right option list by declaration instead of pattern-matching on
    // the field key — the same reason `blocker` exists.
    catalog: "currency",
  },
  {
    key: "payment_type_id",
    label: "Zahlungsart",
    hint: "Aus Bexio übernommen",
    blocker: "missing_payment_type_id",
    control: "catalog",
    catalog: "paymentTypes",
  },
  {
    key: "template_slug",
    label: "Belegvorlage",
    hint: "Bexio-Vorlagen-Slug",
    blocker: "missing_template_slug",
    control: "text",
  },
  {
    key: "bank_account_id",
    label: "Bankkonto",
    hint: "Aus Bexio übernommen",
    blocker: "missing_bank_account_id",
    control: "catalog",
    catalog: "bankAccounts",
  },
  {
    key: "default_tax_id",
    label: "Standard-Steuersatz",
    hint: "Aus Bexio übernommen — gilt für jede Position",
    blocker: "missing_default_tax_id",
    control: "catalog",
    catalog: "taxes",
  },
  {
    key: "mwst_type",
    label: "MWST-Typ",
    hint: "Bexio-MWST-Typ — 0 ist ein gültiger Wert",
    blocker: "missing_mwst_type",
    control: "number",
  },
  {
    key: "mwst_is_net",
    label: "MWST netto",
    blocker: "missing_mwst_is_net",
    control: "tristate",
  },
  {
    key: "show_position_taxes",
    label: "Positionssteuern anzeigen",
    blocker: "missing_show_position_taxes",
    control: "tristate",
  },
  {
    key: "draft_create_start_period",
    label: "Startperiode für Entwürfe",
    hint: "Mietperiode — immer ein Monatserster",
    blocker: "missing_draft_create_start_period",
    control: "month",
  },
];


/* ═══════════════════════════════════════════════════════════════════
 * D2 — the edit surface
 *
 * Everything below turns the stored configuration into form values and back.
 * It is pure and lives here rather than in JSX because the tri-state is the
 * whole difficulty: `mwst_type = 0`, `mwst_is_net = false` and
 * `show_position_taxes = false` are values FAHN runs in production, and every
 * naive idiom on this path — `value || null`, `Boolean("false")`, a bare
 * `JSON.stringify` diff — turns one of them into "not configured" and silently
 * clears a working setting on the next save.
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * A form value that cannot be put on the wire.
 *
 * A Symbol rather than `null` or `undefined` on purpose: those two ARE valid
 * wire values here (they clear a field), so reusing either would make "the user
 * typed nonsense" indistinguishable from "the user cleared this" — and a save
 * would then delete a working configuration. Nothing can serialise a Symbol into
 * JSON by accident either.
 */
export const INVALID = Symbol("invalid form value");

/**
 * The only fields the settings page may write.
 *
 * ONE of the six Bexio ids (`target_user_id`) is deliberately absent: a
 * technically valid but factually wrong id would misconfigure real invoices, and
 * this repository cannot read its catalog to check one. `currency_id` (D3a),
 * `default_tax_id` (D3b), `bank_account_id` (D3c), `payment_type_id` (D3d) and
 * `default_language_id` (D3e) are the exceptions, and only because
 * `/3.0/currencies`, `/3.0/taxes`, `/3.0/banking/accounts`, `/2.0/payment_type`
 * and `/2.0/language` are the provider catalogs we can actually read.
 * `target_user_display_name` is server-owned (the backend clears it when its id
 * changes) and was never client-writable.
 *
 * This list is the structural guarantee that a read-only field cannot reach a
 * PATCH: the form never holds one, so no diff can produce one.
 */
export const EDITABLE_FIELDS = [
  "template_slug",
  "mwst_type",
  "mwst_is_net",
  "show_position_taxes",
  "draft_create_start_period",
  // D3a. The FIRST provider id to become editable, and only because
  // `/3.0/currencies` is the one catalog this repository could then read
  // (`bexio_client.list_currency_catalog`).
  "currency_id",
  // D3b. The second, on the same condition: `/3.0/taxes` is readable AND the
  // provider itself defines which of its rows may go on an invoice
  // (`types=sales_tax&scope=active`), so the list offered here is the list Bexio
  // considers legal.
  "default_tax_id",
  // D3c. The third: `/3.0/banking/accounts` is readable. It documents no
  // active/scope filter, so none is invented — the endpoint returns the accounts
  // Bexio itself shows, and that IS the list. Only the id and the account name
  // ever reach the browser; the IBAN, QR-IBAN, account number and holder address
  // are dropped server-side.
  "bank_account_id",
  // D3d. The fourth: `GET /2.0/payment_type` was verified by one controlled
  // read-only probe against the production tenant. No query string is sent,
  // because none was verified. The remaining two ids stay out until their
  // contracts are verified the same way — a technically valid but factually wrong
  // Bexio id would misconfigure real invoices.
  "payment_type_id",
  // D3e. The fifth: `GET /2.0/language` was verified by one controlled read-only
  // probe against the production tenant, on the same terms — no query string,
  // because none was verified. This is the language of the BEXIO DOCUMENT, not a
  // UI locale and not an `Accept-Language`.
  "default_language_id",
  // AND `target_user_id` IS STILL NOT HERE — but the reason changed in D3f.2.
  // It used to be "its contract is not verified"; four controlled production
  // probes settled that, and its catalog is now readable like the other five.
  // It stays out because its WRITE cannot be provider-free: storing the id also
  // stores the name beside it, the browser may not author that name, and only
  // the server can resolve it. So it saves through its own endpoint, and keeping
  // it out of this list is what lets an admin still fix a template slug while
  // Bexio is down.
];

/** `"2026-10-01"` → `"2026-10"` for `<input type="month">`; nullish → `""`. */
export function monthDateToInput(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value).slice(0, 7);
}

/**
 * `"2026-10"` → `"2026-10-01"`; `""` → `null`; anything else → `INVALID`.
 *
 * Pure string surgery, never a `Date`. This is a civil month, not an instant:
 * `new Date("2026-10-01").toISOString()` is 2026-09-30 west of UTC, which would
 * store the wrong rent period for anyone not on UTC (CLAUDE.md Regel 51).
 */
export function monthInputToDate(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (s === "") return null;
  if (!/^\d{4}-\d{2}$/.test(s)) return INVALID;
  return `${s}-01`;
}

/**
 * Trim, and treat a now-empty string as "clear this field".
 *
 * Mirrors the backend normalizer exactly, which is what makes `" abc "` over a
 * stored `"abc"` register as NOT dirty: without it the page would send a patch
 * the server normalises straight back to the stored value — a save that visibly
 * does nothing.
 */
export function normalizeTemplateSlug(raw) {
  if (raw === null || raw === undefined) return null;
  const t = String(raw).trim();
  return t === "" ? null : t;
}

/**
 * `""` → `null`, a non-negative integer string → that integer, anything else →
 * `INVALID`.
 *
 * `"0"` MUST survive as `0`: it is the value FAHN runs. And `"abc"`, `"1.5"` and
 * `"-1"` must NOT collapse to `null`, because `null` means "clear it" — a typo
 * would otherwise wipe a configured MWST type. They are reported as invalid and
 * block the save instead.
 */
export function normalizeNullableInteger(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw >= 0 ? raw : INVALID;
  }
  const s = String(raw).trim();
  if (s === "") return null;
  if (!/^\d+$/.test(s)) return INVALID;
  return Number(s);
}

/**
 * A provider-catalog select value → the form value. `""` → `null`.
 *
 * The stored value is an INTEGER id, so the DOM's string has to become one here,
 * at the control boundary — the same place the tri-state converts. Anything that
 * is not a positive integer becomes `null` rather than a guess; unlike a text
 * field a select cannot produce a half-typed value, so the only string it can
 * emit that is not an option is the empty one, which means "not configured".
 */
export function normalizeCatalogId(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  }
  const s = String(raw).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n > 0 ? n : null;
}

/** Select value → form value. `""` → `null`, `"true"` → `true`, `"false"` → `false`. */
export function normalizeTriState(raw) {
  if (raw === true || raw === false) return raw;
  if (raw === "true") return true;
  if (raw === "false") return false;
  return null;
}

/** Form value → select value. `null` → `""`, so the empty option is selected. */
export function triStateToSelectValue(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  return "";
}

/**
 * `config.values` → the form shape.
 *
 * Text-ish controls hold strings (that is what a DOM input gives back), the
 * tri-states hold real `true`/`false`/`null`. `mwst_type` is stored as a STRING
 * so that a half-typed or invalid entry stays distinguishable from an empty one
 * — see `normalizeNullableInteger`.
 */
export function configValuesToForm(values) {
  const v = values || {};
  const num = (x) => (x === null || x === undefined ? "" : String(x));
  return {
    template_slug: v.template_slug === null || v.template_slug === undefined
      ? ""
      : String(v.template_slug),
    mwst_type: num(v.mwst_type),
    mwst_is_net: v.mwst_is_net === true || v.mwst_is_net === false ? v.mwst_is_net : null,
    show_position_taxes:
      v.show_position_taxes === true || v.show_position_taxes === false
        ? v.show_position_taxes
        : null,
    draft_create_start_period: monthDateToInput(v.draft_create_start_period),
    // A real int, not a string: a select can only emit the values we put in it,
    // so there is no half-typed state to keep distinguishable (which is the only
    // reason `mwst_type` is held as text).
    currency_id: normalizeCatalogId(v.currency_id),
    default_tax_id: normalizeCatalogId(v.default_tax_id),
    bank_account_id: normalizeCatalogId(v.bank_account_id),
    payment_type_id: normalizeCatalogId(v.payment_type_id),
    default_language_id: normalizeCatalogId(v.default_language_id),
  };
}

/** The whole form, in wire values. Any unusable entry appears as `INVALID`. */
export function formToWireValues(form) {
  const f = form || {};
  return {
    template_slug: normalizeTemplateSlug(f.template_slug),
    mwst_type: normalizeNullableInteger(f.mwst_type),
    mwst_is_net: normalizeTriState(f.mwst_is_net),
    show_position_taxes: normalizeTriState(f.show_position_taxes),
    draft_create_start_period: monthInputToDate(f.draft_create_start_period),
    currency_id: normalizeCatalogId(f.currency_id),
    default_tax_id: normalizeCatalogId(f.default_tax_id),
    bank_account_id: normalizeCatalogId(f.bank_account_id),
    payment_type_id: normalizeCatalogId(f.payment_type_id),
    default_language_id: normalizeCatalogId(f.default_language_id),
  };
}

/**
 * Per-field German messages for entries that cannot be sent. Empty when clean.
 *
 * These BLOCK the save rather than being silently dropped: dropping them would
 * let a save succeed while quietly ignoring what the operator typed.
 */
export function formFieldErrors(form) {
  const wire = formToWireValues(form);
  const errors = {};
  if (wire.mwst_type === INVALID) {
    errors.mwst_type = "Bitte eine ganze Zahl ab 0 eingeben.";
  }
  if (wire.draft_create_start_period === INVALID) {
    errors.draft_create_start_period = "Bitte einen gültigen Monat wählen.";
  }
  if (typeof wire.template_slug === "string" && wire.template_slug.length > 64) {
    errors.template_slug = "Höchstens 64 Zeichen.";
  }
  return errors;
}

/** `undefined` and `null` mean the same thing on this wire; compare them as one. */
function canonical(value) {
  return value === undefined ? null : value;
}

/**
 * Only the fields whose value actually changed, in wire form.
 *
 * `Object.is` over canonicalised values, never `JSON.stringify`: a
 * stringify-based diff calls `undefined` different from `null` and then drops
 * the key from the body, so the field would count as changed and never be sent —
 * a save that silently does nothing.
 *
 * Read-only fields cannot appear here by construction: they are not in
 * `EDITABLE_FIELDS` and `formToWireValues` never produces them.
 */
export function buildDirtyPatch(form, initial) {
  if (!form || !initial) return {};
  const now = formToWireValues(form);
  const before = formToWireValues(initial);
  const patch = {};
  for (const key of EDITABLE_FIELDS) {
    const next = now[key];
    // An unusable entry is never sent; `formFieldErrors` blocks the save.
    if (next === INVALID) continue;
    if (!Object.is(canonical(next), canonical(before[key]))) {
      patch[key] = next;
    }
  }
  return patch;
}

/** True when a save would send at least one field. */
export function isFormDirty(form, initial) {
  return Object.keys(buildDirtyPatch(form, initial)).length > 0;
}

/**
 * What to tell the operator when a save fails.
 *
 * Status-driven, because the three refusals mean genuinely different things and
 * a 404 in particular must not read as a generic hiccup — it means this
 * organization has no Bexio connection at all. The 422 case reuses the server's
 * own joined validation text (`adminErrorFrom` already assembles it from
 * `detail[].msg`); everything else gets one sentence and never a raw
 * database or constraint string.
 */
export function saveErrorMessage(error) {
  const status = error?.status;
  if (status === 403) return "Keine Berechtigung zum Speichern.";
  if (status === 404) return "Bexio-Verbindung nicht gefunden.";
  if (status === 422) {
    const msg = typeof error?.message === "string" ? error.message.trim() : "";
    return msg || "Die Eingaben wurden vom Server abgelehnt.";
  }
  return "Die Einstellungen konnten nicht gespeichert werden.";
}


/* ═══════════════════════════════════════════════════════════════════
 * D3f.2 — the internal contact, which saves on its own
 *
 * `target_user_id` is the one configuration value whose write asks Bexio a
 * question: the server resolves the chosen id into `target_user_display_name`
 * so the browser never names the person it picks. That is why it has its own
 * endpoint, and why the helpers below are separate from the form ones above —
 * nothing here feeds `buildDirtyPatch`, and nothing there can reach this field.
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * True when saving the internal contact would actually change something.
 *
 * Both sides go through `normalizeCatalogId` first, so a select's `"3"` and a
 * stored `3` compare equal and `""` and `null` are one value. Without that the
 * button would light up the moment the page rendered — the DOM hands back
 * strings, the config holds integers — and a "save" would send the value that
 * was already stored.
 *
 * `Object.is` on the normalised pair rather than `==`: loose equality would also
 * call `null` and `0` equal, and `0` is not a valid Bexio user id but IS the
 * kind of value a broken normaliser produces.
 */
export function isTargetUserDirty(draft, persisted) {
  return !Object.is(normalizeCatalogId(draft), normalizeCatalogId(persisted));
}

/**
 * What to tell the operator when the dedicated contact save fails.
 *
 * Status-driven like `saveErrorMessage`, but the 409 splits in two and the split
 * is the whole reason this helper exists. Both mean "conflict", and they ask the
 * operator for opposite things:
 *
 * - `target_user_not_available` — the provider answered correctly and that
 *   person is not in this mandant any more. Fixable here, by picking someone
 *   else. This is the live production case: the stored id resolves to a 404.
 * - anything else 409 — there is no active Bexio connection at all. Nothing to
 *   pick; the connection has to be sorted out first.
 *
 * They are told apart by the backend's machine-readable `detail.code`, never by
 * matching German prose, which is display text and will be reworded.
 *
 * No provider path, status line or raw body is ever shown: those name internal
 * endpoints and would tell an operator nothing they can act on.
 */
export function targetUserSaveErrorMessage(error) {
  const status = error?.status;
  if (status === 409) {
    if (error?.code === TARGET_USER_NOT_AVAILABLE) {
      return "Der ausgewählte Bexio-Benutzer ist nicht mehr verfügbar. Bitte Auswahl aktualisieren.";
    }
    return "Bexio-Verbindung ist nicht aktiv.";
  }
  if (status === 503) {
    return "Bexio ist derzeit nicht erreichbar bzw. die Verbindung muss geprüft werden.";
  }
  if (status === 502) return "Der Bexio-Benutzer konnte nicht geprüft werden.";
  if (status === 403) return "Keine Berechtigung zum Speichern.";
  if (status === 404) return "Bexio-Verbindung nicht gefunden.";
  return "Der Ansprechpartner konnte nicht gespeichert werden.";
}

/**
 * The backend's stable discriminator for "that Bexio user is gone".
 *
 * Named once here so no component matches on the sentence. The German message
 * beside it in the response is what a person reads; this is what code branches
 * on.
 */
export const TARGET_USER_NOT_AVAILABLE = "target_user_not_available";
